import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Popover, PopoverTrigger, PopoverContent } from '@tyndo/ui-kit';
import { AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import { remoteHealth, type RemoteHealth } from '../observability/remoteHealth';

export const REMOTE_STATUS_LABEL: Record<string, string> = {
  unreachable: 'Module unreachable',
  shape_mismatch: 'Module incompatible',
  version_skew: 'Version mismatch',
  empty: 'Module exposed nothing',
};

/**
 * Backoff state line for a degraded remote: how many re-attempts have been made
 * and a live countdown to the scheduler's next auto-retry (`nextRetryAt`, a ms
 * epoch from the exponential-backoff schedule). The countdown ticks once a
 * second while mounted (only while the operator has the popover open) and falls
 * back to a "retries exhausted" line once auto-retry stops. Strings use i18n.
 */
function FederationRetryStatus({ health }: { health: RemoteHealth }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const canRetry = remoteHealth.canRetry(health.federatedName);
  const secondsLeft =
    health.nextRetryAt != null ? Math.max(0, Math.ceil((health.nextRetryAt - now) / 1000)) : null;

  let schedule: string | null = null;
  if (!canRetry) {
    schedule = t('federation.retriesExhausted');
  } else if (secondsLeft != null) {
    schedule = secondsLeft > 0 ? t('federation.nextRetry', { seconds: secondsLeft }) : t('federation.nextRetryDue');
  }

  return (
    <>
      {health.attempts > 0 && (
        <div className="flex gap-1.5">
          <dt className="shrink-0 text-muted-foreground">{t('federation.attempts', { count: health.attempts })}</dt>
        </div>
      )}
      {schedule && (
        <div className="flex gap-1.5">
          <dd className="text-muted-foreground" aria-live="polite">
            {schedule}
          </dd>
        </div>
      )}
    </>
  );
}

/**
 * Sidebar badge for a degraded federated module: a red marker that opens an
 * error popover (module id + status + last error) with a per-module re-import
 * retry and a hard reload as last resort.
 *
 * DOCUMENTED FLOOR — the trigger is a focusable `span[role=button]`, not the
 * ui-kit `Button`. It renders INSIDE the Sidebar item's own button element, and
 * a nested button is invalid DOM (HTML disallows interactive content inside a
 * button; browsers reparent it, and AT announces one control, not two). No
 * variant or class can express that, so the affordance is hand-rolled: it
 * carries the same focus-visible ring the kit applies, is keyboard-activated
 * via Enter/Space, and stops propagation so opening it never also navigates.
 */
export function DegradedModuleBadge({ health, label }: { health: RemoteHealth; label: string }) {
  const { t } = useTranslation();
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          aria-label={t('a11y.moduleLoadFailed', { module: label })}
          aria-haspopup="dialog"
          onClick={stop}
          onPointerDown={stop}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
          }}
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-alarm-critical/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <AlertTriangle className="h-3.5 w-3.5 text-alarm-critical" aria-hidden="true" />
        </span>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        className="w-72 p-3 text-sm"
        onClick={stop}
      >
        <p className="flex items-center gap-1.5 font-medium text-foreground">
          <AlertTriangle className="h-4 w-4 shrink-0 text-alarm-critical" />
          {t('federation.moduleLoadFailed')}
        </p>
        <dl className="mt-2 space-y-1 text-xs">
          <div className="flex gap-1.5">
            <dt className="shrink-0 text-muted-foreground">{t('federation.moduleId')}:</dt>
            <dd className="t-mono break-all text-foreground">
              {health.moduleId ?? health.federatedName}
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="shrink-0 text-muted-foreground">{t('federation.lastError')}:</dt>
            <dd className="break-words text-foreground">
              {health.error ?? REMOTE_STATUS_LABEL[health.status] ?? health.status}
            </dd>
          </div>
          <FederationRetryStatus health={health} />
        </dl>
        <div className="mt-3 flex items-center gap-2">
          {remoteHealth.canRetry(health.federatedName) && (
            <Button
              type="button"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                void remoteHealth.retry(health.federatedName);
              }}
              disabled={health.retrying}
              className="h-auto whitespace-normal [&_svg]:size-3.5 inline-flex items-center gap-1 rounded-md border border-border bg-surface-raised px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
            >
              {health.retrying ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              {health.retrying ? t('federation.retrying') : t('federation.retry')}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              window.location.reload();
            }}
            className="h-auto whitespace-normal font-normal inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            {t('federation.reloadPage')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
